import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@diverge/ui/ui/alert";

import Integrations from "../pages/Integrations";
import { RequireAuth } from "../utils/RequireAuth";

/**
 * Shown to a signed-in user who followed the Shopify app's setup link but is
 * not an owner of their Diverge organization. A silent redirect to / left
 * merchants (and the App Store reviewer) with no way to see why setup stopped.
 */
function ShopifySetupNeedsOwner({ shopDomain }: { shopDomain: string }) {
  return (
    <main className="h-full overflow-y-auto bg-background p-6 lg:p-8">
      <div className="mx-auto max-w-2xl">
        <Alert>
          <AlertTriangleIcon />
          <AlertTitle>Shopify setup needs an organization owner</AlertTitle>
          <AlertDescription>
            <p>
              Linking the Shopify store <strong>{shopDomain}</strong> to a chatbot can only be
              completed by an owner of your Diverge organization. Ask an owner to sign in and finish
              the setup, or contact team@askdiverge.ai to be made an owner.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </main>
  );
}

interface IntegrationsSearch {
  shopify_connected?: string;
  shopify_error?: string;
  shopify_shop?: string;
  shopify_warnings?: string;
}

function parseIntegrationsSearch(search: Record<string, unknown>): IntegrationsSearch {
  return Object.fromEntries(
    ["shopify_connected", "shopify_error", "shopify_shop", "shopify_warnings"]
      .filter((key) => typeof search[key] === "string")
      .map((key) => [key, search[key]]),
  ) as IntegrationsSearch;
}

export const Route = createFileRoute("/_shell/integrations")({
  validateSearch: parseIntegrationsSearch,
  component: IntegrationsRoute,
});

function IntegrationsRoute() {
  const search = Route.useSearch();
  const loginReturnTo = search.shopify_shop
    ? `/integrations?shopify_shop=${encodeURIComponent(search.shopify_shop)}`
    : undefined;

  return (
    <RequireAuth
      require="integrations"
      loginReturnTo={loginReturnTo}
      fallback={
        search.shopify_shop ? (
          <ShopifySetupNeedsOwner shopDomain={search.shopify_shop} />
        ) : undefined
      }
    >
      <Integrations />
    </RequireAuth>
  );
}
